import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { studentSchema, StudentFormValues } from "../schema/studentSchema";
import { useLanguage } from "@/components/LanguageContext";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguagePath } from "@/utils/language";

interface UseStudentFormProps {
  id?: string;
  initialData?: StudentFormValues & { id: string };
}

interface SupabaseUser {
  id: string;
  email?: string;
  [key: string]: any;
}

export const useStudentForm = ({ id, initialData }: UseStudentFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { t } = useTranslations("students");
  const { getPath } = useLanguagePath();

  const form = useForm<StudentFormValues>({
    resolver: zodResolver(studentSchema),
    defaultValues: initialData || {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      birth_date: "",
      height: "",
      weight: "",
      medical_conditions: "",
      emergency_contact: "",
      preferred_training_time: undefined,
      fitness_level: "iniciante",
      initial_assessment_date: "",
      initial_assessment_notes: "",
      preferred_training_type: undefined,
      goals: "",
      notes: "",
      monthly_fee: "",
      payment_status: undefined,
      payment_method: undefined,
      payment_notes: "",
      has_schedule_access: true,
      send_account_email: true,
    },
  });

  const checkExistingAuthAccount = async (email: string) => {
    try {
      const { data: authData, error: authError } = await supabase.auth.admin.listUsers();

      if (authError) {
        console.error("Erro ao verificar conta de autenticação:", authError);
        return null;
      }

      if (authData && authData.users) {
        const users = authData.users as unknown as SupabaseUser[];
        const user = users.find(u => u.email === email);
        
        if (user) {
          console.log("Encontrada conta de autenticação existente:", user);
          
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('id, is_student')
            .eq('id', user.id)
            .single();

          if (profileError) {
            console.error("Erro ao verificar perfil existente:", profileError);
            return null;
          }

          if (!profile) {
            console.log("Perfil não encontrado para o usuário existente");
            return null;
          }

          return {
            id: user.id,
            is_student: profile.is_student
          };
        }
      }
      
      return null;
    } catch (error) {
      console.error("Erro ao verificar conta existente:", error);
      return null;
    }
  };

  const checkExistingProfileByEmail = async (email: string) => {
    try {
      const { data: student, error: studentError } = await supabase
        .from('students')
        .select('auth_id')
        .eq('email', email)
        .not('auth_id', 'is', null)
        .single();

      if (studentError && studentError.code !== 'PGRST116') {
        console.error("Erro ao verificar aluno existente:", studentError);
      }

      if (student?.auth_id) {
        console.log("Encontrado aluno com auth_id:", student.auth_id);
        
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('id, is_student')
          .eq('id', student.auth_id)
          .single();

        if (profileError) {
          console.error("Erro ao verificar perfil existente:", profileError);
          return null;
        }

        return {
          id: profile.id,
          is_student: profile.is_student
        };
      }
      
      return null;
    } catch (error) {
      console.error("Erro ao verificar perfil pelo email:", error);
      return null;
    }
  };

  const checkExistingUserByEmail = async (email: string) => {
    try {
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, is_student, first_name, last_name')
        .limit(10);
        
      if (profileError) {
        console.error("Erro ao buscar perfis:", profileError);
        return null;
      }

      if (profiles && profiles.length > 0) {
        const { data: authData, error: authError } = await supabase.auth.admin.listUsers();
        
        if (authError) {
          console.error("Erro ao listar usuários auth:", authError);
          return null;
        }
        
        if (authData?.users) {
          const users = authData.users as unknown as SupabaseUser[];
          
          const matchingUser = users.find(u => u.email === email);
          
          if (matchingUser) {
            console.log("Encontrado usuário pelo email:", matchingUser);
            
            const matchingProfile = profiles.find(p => p.id === matchingUser.id);
            
            if (matchingProfile) {
              return {
                id: matchingUser.id,
                is_student: matchingProfile.is_student
              };
            } else {
              console.log("Usuário encontrado, mas sem perfil correspondente");
            }
          }
        }
      }
      
      return null;
    } catch (error) {
      console.error("Erro ao verificar usuário pelo email:", error);
      return null;
    }
  };

  const handleSubmit = async (data: StudentFormValues) => {
    try {
      setIsSubmitting(true);
      console.log("Submitting student data:", data);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("Usuário não autenticado");
      }

      let existingAuthUser = null;
      if (data.email) {
        existingAuthUser = await checkExistingProfileByEmail(data.email);
        
        if (!existingAuthUser) {
          console.log("Tentando método alternativo de verificação pelo email");
          existingAuthUser = await checkExistingUserByEmail(data.email);
        }
        
        if (!existingAuthUser) {
          console.log("Tentando método alternativo para verificar conta existente");
          existingAuthUser = await checkExistingAuthAccount(data.email);
        }
      }

      console.log("Resultado da verificação de conta existente:", existingAuthUser);

      const { send_account_email, ...dataWithoutSendEmail } = data;
      
      const supabaseData = {
        ...dataWithoutSendEmail,
        first_name: data.first_name,
        last_name: data.last_name,
        height: data.height ? parseFloat(data.height) : null,
        weight: data.weight ? parseFloat(data.weight) : null,
        monthly_fee: data.monthly_fee ? parseFloat(data.monthly_fee) : null,
        birth_date: data.birth_date || null,
        initial_assessment_date: data.initial_assessment_date || null,
        trainer_id: user.id,
        fitness_level: data.fitness_level || "iniciante",
        preferred_training_time: data.preferred_training_time || null,
        preferred_training_type: data.preferred_training_type || null,
        payment_status: data.payment_status || null,
        payment_method: data.payment_method || null,
        has_schedule_access: data.has_schedule_access !== undefined ? data.has_schedule_access : true,
        auth_id: existingAuthUser ? existingAuthUser.id : null,
        auth_account_created: existingAuthUser ? true : false
      };

      let error;
      let studentId;
      
      if (id) {
        console.log("Updating student with ID:", id);
        const { error: updateError, data: updatedStudent } = await supabase
          .from("students")
          .update(supabaseData)
          .eq("id", id)
          .select('id');
        error = updateError;
        studentId = id;
        
        if (!error) {
          toast.success(t("notifications.updated"));
          queryClient.invalidateQueries({ queryKey: ["students"] });
          queryClient.invalidateQueries({ queryKey: ["students-count"] });
        }
      } else {
        console.log("Creating new student");
        const { error: insertError, data: insertedData } = await supabase
          .from("students")
          .insert(supabaseData)
          .select('id');
        error = insertError;
        studentId = insertedData?.[0]?.id;
        
        if (!error) {
          toast.success(t("notifications.created"));
          queryClient.invalidateQueries({ queryKey: ["students"] });
          queryClient.invalidateQueries({ queryKey: ["students-count"] });
        }
      }

      if (error) throw error;

      if (!existingAuthUser && data.email && studentId && data.send_account_email) {
        try {
          console.log("Enviando convite por email para:", data.email);
          
          const { data: inviteResponse, error: inviteError } = await supabase.functions.invoke('invite-student', {
            body: {
              email: data.email,
              firstName: data.first_name,
              lastName: data.last_name,
              studentId: studentId,
              hasScheduleAccess: data.has_schedule_access
            }
          });
          
          if (inviteError) {
            console.error("Erro ao enviar convite:", inviteError);
            toast.error("Erro ao enviar convite por email");
          } else if (inviteResponse?.success) {
            console.log("Convite enviado com sucesso");
            toast.success("Convite enviado por email para o aluno!");
            
            // Recarregar dados para mostrar o estado atualizado
            queryClient.invalidateQueries({ queryKey: ["students"] });
          } else {
            console.error("Resposta de erro do convite:", inviteResponse);
            toast.error(inviteResponse?.error || "Erro ao enviar convite por email");
          }
        } catch (inviteError) {
          console.error("Erro ao enviar convite:", inviteError);
          toast.error("Erro ao enviar convite por email");
        }
      }

      if (existingAuthUser && data.has_schedule_access !== undefined) {
        try {
          const { error: updateAuthError } = await supabase.auth.admin.updateUserById(
            existingAuthUser.id,
            {
              user_metadata: {
                has_schedule_access: data.has_schedule_access
              }
            }
          );
          
          if (updateAuthError) {
            console.error("Erro ao atualizar permissões de agenda:", updateAuthError);
          } else {
            console.log("Permissões de agenda atualizadas com sucesso");
          }
        } catch (error) {
          console.error("Erro ao atualizar permissões de agenda:", error);
        }
      }

      navigate(getPath("/dashboard/students"));
    } catch (error: any) {
      console.error("Error submitting student:", error);
      toast.error(error.message || "Erro ao salvar aluno");
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = () => {
    const values = form.getValues();
    handleSubmit(values);
  };

  return {
    form,
    isSubmitting,
    onSubmit,
  };
};
